import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent
} from '@mui/material';
import ShieldIcon from '@mui/icons-material/Shield';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import WarningIcon from '@mui/icons-material/Warning';
import axiosInstance from '../../utils/api';
import { useLocation, useNavigate } from 'react-router-dom';

export default function MFASettings() {
  const location = useLocation();
  const navigate = useNavigate();
  const isForced = location.state?.forced || false;
  const fromPath = location.state?.from;

  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [setupDialogOpen, setSetupDialogOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isStaff, setIsStaff] = useState(false);

  const steps = ['Scan QR Code', 'Verify Code', 'Complete'];

  useEffect(() => {
    fetchMFAStatus();
  }, []);

  const fetchMFAStatus = async () => {
    try {
      const response = await axiosInstance.get('/api/mfa/status/');
      setMfaEnabled(response.data.mfa_enabled);
      setIsStaff(response.data.is_staff);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch MFA status:', error);
      setLoading(false);
    }
  };

  const handleSetupStart = async () => {
    setError('');
    setSetupDialogOpen(true);
    setActiveStep(0);

    try {
      const response = await axiosInstance.post('/api/mfa/setup/');
      setQrCode(response.data.qr_code);
      setSecret(response.data.secret);
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to generate QR code');
    }
  };

  const handleVerify = async () => {
    setError('');

    if (verificationCode.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }

    try {
      await axiosInstance.post('/api/mfa/verify/', {
        code: verificationCode
      });

      setActiveStep(2);
      setSuccess('Two-factor authentication has been enabled successfully!');
      setMfaEnabled(true);

      // Close dialog and redirect after 2 seconds
      setTimeout(() => {
        setSetupDialogOpen(false);
        setVerificationCode('');
        setActiveStep(0);

        // If they were forced here, redirect back to where they came from
        if (isForced && fromPath) {
          navigate(fromPath);
        }
      }, 2000);
    } catch (error) {
      setError(error.response?.data?.error || 'Invalid verification code');
    }
  };

  const handleDisable = async () => {
    if (!window.confirm('Are you sure you want to disable two-factor authentication?')) {
      return;
    }

    try {
      await axiosInstance.post('/api/mfa/disable/');
      setMfaEnabled(false);
      setSuccess('Two-factor authentication has been disabled');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to disable MFA');
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ShieldIcon fontSize="large" />
          Two-Factor Authentication
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Add an extra layer of security to your admin account
        </Typography>
      </Box>

      {isForced && !mfaEnabled && (
        <Alert severity="warning" sx={{ mb: 3 }} icon={<WarningIcon />}>
          <Typography variant="body1" gutterBottom>
            Two-factor authentication is required for admin accounts
          </Typography>
          <Typography variant="body2">
            Please set up MFA to continue accessing the admin dashboard.
          </Typography>
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {error && !setupDialogOpen && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" gutterBottom>
              Status: {mfaEnabled ? (
                <Box component="span" sx={{ color: 'success.main' }}>
                  Enabled ✓
                </Box>
              ) : (
                <Box component="span" sx={{ color: 'warning.main' }}>
                  Disabled
                </Box>
              )}
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {mfaEnabled
                ? 'Your account is protected with two-factor authentication. You will need to enter a code from your authenticator app when logging in.'
                : 'Enable two-factor authentication to add an extra layer of security to your account.'}
            </Typography>

            {!mfaEnabled ? (
              <Button
                variant="contained"
                color="primary"
                startIcon={<QrCode2Icon />}
                onClick={handleSetupStart}
                size="large"
              >
                Enable Two-Factor Authentication
              </Button>
            ) : (
              <>
                {isStaff ? (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      Two-factor authentication is required for all admin accounts and cannot be disabled.
                    </Typography>
                  </Alert>
                ) : (
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleDisable}
                  >
                    Disable Two-Factor Authentication
                  </Button>
                )}
              </>
            )}
          </Box>
        </Box>
      </Paper>

      {/* Setup Dialog */}
      <Dialog
        open={setupDialogOpen}
        onClose={() => !activeStep === 2 && setSetupDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Enable Two-Factor Authentication
        </DialogTitle>
        <DialogContent>
          <Stepper activeStep={activeStep} sx={{ mb: 4, mt: 2 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {activeStep === 0 && (
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
                Scan this QR code with your authenticator app
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Use Google Authenticator, Authy, or any TOTP-compatible app
              </Typography>

              {qrCode ? (
                <Box sx={{ mb: 3 }}>
                  <img src={qrCode} alt="MFA QR Code" style={{ maxWidth: '100%' }} />
                </Box>
              ) : (
                <CircularProgress />
              )}

              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="caption" color="text.secondary">
                    Can't scan? Enter this code manually:
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', mt: 1, wordBreak: 'break-all' }}>
                    {secret}
                  </Typography>
                </CardContent>
              </Card>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
            </Box>
          )}

          {activeStep === 1 && (
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body1" gutterBottom sx={{ mb: 3 }}>
                Enter the 6-digit code from your authenticator app
              </Typography>

              <TextField
                fullWidth
                label="Verification Code"
                value={verificationCode}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setVerificationCode(value);
                }}
                inputProps={{
                  maxLength: 6,
                  style: { textAlign: 'center', fontSize: '24px', letterSpacing: '8px' }
                }}
                autoFocus
                sx={{ mb: 2 }}
              />

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
            </Box>
          )}

          {activeStep === 2 && (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <VerifiedUserIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
              <Typography variant="h6" color="success.main" gutterBottom>
                Two-Factor Authentication Enabled!
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Your account is now protected with an extra layer of security.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {activeStep === 0 && (
            <>
              <Button onClick={() => setSetupDialogOpen(false)}>
                Cancel
              </Button>
              <Button variant="contained" onClick={() => setActiveStep(1)}>
                Next: Verify Code
              </Button>
            </>
          )}
          {activeStep === 1 && (
            <>
              <Button onClick={() => setActiveStep(0)}>
                Back
              </Button>
              <Button
                variant="contained"
                onClick={handleVerify}
                disabled={verificationCode.length !== 6}
              >
                Verify & Enable
              </Button>
            </>
          )}
          {activeStep === 2 && (
            <Button variant="contained" onClick={() => setSetupDialogOpen(false)}>
              Done
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
}
